import { RefreshCw, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api, isApiError } from '@/api';
import type { DepositFormatStatus, DepositStatus, DepositType, EscrowDeposit, EscrowDepositDetail, EscrowDepositList } from '@/api/areas/market';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, FilterBar, Pager, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { CorrelationLink } from '@/components/Ids';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { CopyText, EmptyState, Loading } from '@/components/States';
import { useRetained } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { EnumBadge, SelectField, useEnumLabel, useEnumOptions } from '../../compliance/ui';
import { DEPOSIT_STATUS, formatBytes, VERIFICATION_STATUS } from '../shared';
import { policyLabel } from '@/lib/labels';

const BASE = '/compliance/icann/rde';
const PAGE = 100;
const DEPOSIT_TYPES: DepositType[] = ['FULL', 'INCREMENTAL'];
const ATTEMPT_TONES = {
  DELIVERED: { tone: 'ok' },
  UNKNOWN: { tone: 'serious' },
  FAILED: { tone: 'crit' },
  SENDING: { tone: 'pending' },
} as const;

/** Official format name, or a warning that the deposit uses the internal draft format. */
function FormatBadge({ status, format }: { status: DepositFormatStatus; format?: string }) {
  const { t } = useTranslation('compliance');
  return status === 'OFFICIAL' ? (
    <Badge tone="ok">{format ?? t('rde.draft.official')}</Badge>
  ) : (
    <Badge tone="warn" title={t('rde.draft.badgeHint')}>
      {format ? t('rde.draft.badgeFormat', { format }) : t('rde.draft.badge')}
    </Badge>
  );
}

function DraftFormatNotice() {
  const { t } = useTranslation('compliance');
  return (
    <Callout tone="warn" title={t('rde.draft.title')}>
      {t('rde.draft.body')}
    </Callout>
  );
}

/** Policy notes the console knows, by their opening words: shown translated, the policy's own text in a tooltip. */
const KNOWN_NOTES: [prefix: string, key: 'draftFormat' | 'schedule' | 'utc'][] = [
  ['The deposit format ', 'draftFormat'],
  ['Schedule follows the standard Registrar Data Escrow', 'schedule'],
  ['Slots are computed in UTC', 'utc'],
];

type Lookup = (key: string, o?: Record<string, unknown>) => string;

/** "P7D" -> "7 ngày", "PT15M" -> "15 phút"; other values unchanged. */
function durationText(t: Lookup, v: string): string {
  const m = /^P(?:(\d+)([DW]))?(?:T(\d+)([HMS]))?$/.exec(v);
  if (!m || (!m[1] && !m[3])) return v;
  return [m[1] ? t(`rde.policy.units.${m[2]}`, { count: Number(m[1]) }) : null, m[3] ? t(`rde.policy.units.${m[4]}`, { count: Number(m[3]) }) : null]
    .filter(Boolean)
    .join(' ');
}

function PolicyCard({ policy }: { policy: EscrowDepositList['policy'] }) {
  const { t } = useTranslation('compliance');
  const tr = t as unknown as Lookup;
  const label = useEnumLabel();
  if (!policy) {
    return (
      <Callout tone="crit" title={t('rde.noPolicyTitle')}>
        {t('rde.noPolicy')}
      </Callout>
    );
  }
  const p = (policy.parameters ?? {}) as Record<string, unknown>;
  const sched = (k: string) => {
    const v = p[k] as Record<string, unknown> | undefined;
    const value = (a: string, b: unknown) => {
      const text = String(b);
      if (a === 'weekday') return tr(`rde.policy.weekdays.${text}`, { defaultValue: text });
      return durationText(tr, text);
    };
    return v
      ? Object.entries(v)
          .map(([a, b]) => tr(`rde.policy.param.${a}`, { value: value(a, b), defaultValue: `${a} ${String(b)}` }))
          .join(', ')
      : null;
  };
  return (
    <Section title={t('rde.policy.title')}>
      <KeyValue
        columns={3}
        rows={[
          [
            t('rde.policy.policy'),
            <EntityRef key="p" label={policyLabel(policy.policy_key)} to={`/compliance/policies/${encodeURIComponent(policy.policy_key)}`} sub={policy.version} />,
          ],
          [t('rde.policy.effective'), formatDateTime(policy.effective_from)],
          [t('rde.policy.format'), typeof p.format === 'string' ? <code key="f">{p.format}</code> : null],
          [t('rde.policy.scope'), typeof p.scope === 'string' ? label('escrowScope', p.scope) : null],
          [t('rde.policy.full'), sched('full')],
          [t('rde.policy.incremental'), sched('incremental')],
          [t('rde.policy.delivery'), sched('delivery')],
        ]}
      />
      {policy.notes.length ? (
        <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {policy.notes.map((n) => {
            const known = KNOWN_NOTES.find(([prefix]) => n.startsWith(prefix));
            return known ? (
              <li key={n} title={t('rde.policy.originalHint', { text: n })}>
                {t(`rde.notes.${known[1]}`)}
              </li>
            ) : (
              <li key={n}>{n}</li>
            );
          })}
        </ul>
      ) : null}
    </Section>
  );
}

/** `/compliance/icann/rde`: registrar data escrow deposits (spec §43), newest watermark first. */
export function RdePage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = useEnumOptions();
  useDocumentTitle(t('rde.title'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as DepositStatus | '';
  const type = (params.get('type') ?? '') as DepositType | '';
  const [cursor, setCursor] = useState<EscrowDepositList['next']>(null);
  const [trail, setTrail] = useState<EscrowDepositList['next'][]>([]);
  const result = useApi(
    (signal) =>
      api.listEscrowDeposits(
        {
          status: status || undefined,
          deposit_type: type || undefined,
          before: cursor?.before,
          before_type: cursor?.before_type,
          limit: PAGE,
        },
        { signal },
      ),
    [status, type, cursor],
  );
  const data = useRetained(result.status === 'success' ? result.data : undefined);
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
    setCursor(null);
    setTrail([]);
  };

  const columns: Column<EscrowDeposit>[] = [
    {
      key: 'watermark',
      header: t('rde.col.watermark'),
      cell: (d) => (
        <div className="whitespace-nowrap">
          <Link to={`${BASE}/${encodeURIComponent(d.id)}`} className="font-medium text-primary hover:underline">
            {formatDateTime(d.watermark)}
          </Link>
          {d.since_watermark ? <div className="text-xs text-muted-foreground">{t('rde.since', { at: formatDateTime(d.since_watermark) })}</div> : null}
        </div>
      ),
    },
    {
      key: 'type',
      header: t('rde.col.type'),
      cell: (d) => label('depositType', d.deposit_type),
    },
    {
      key: 'status',
      header: t('rde.col.status'),
      cell: (d) => (
        <>
          <EnumBadge group="depositStatus" value={d.status} tones={DEPOSIT_STATUS} />
          {d.last_error_code ? <div className="mt-1 font-mono text-xs">{d.last_error_code}</div> : null}
        </>
      ),
    },
    {
      key: 'records',
      header: t('rde.col.records'),
      numeric: true,
      cell: (d) => (
        <>
          {d.record_count ?? '—'}
          {d.deletion_count ? <div className="text-xs text-muted-foreground">{t('rde.deleted', { count: d.deletion_count })}</div> : null}
        </>
      ),
    },
    {
      key: 'content',
      header: t('rde.col.content'),
      numeric: true,
      cell: (d) => (
        <span className="whitespace-nowrap">
          {d.content_files
            ? t('rde.files', {
                size: formatBytes(d.content_bytes),
                count: d.content_files,
              })
            : '—'}
        </span>
      ),
    },
    {
      key: 'delivered',
      header: t('rde.col.delivered'),
      numeric: true,
      cell: (d) => (
        <span className="whitespace-nowrap">
          {d.delivery_files
            ? t('rde.files', {
                size: formatBytes(d.delivery_bytes),
                count: d.delivery_files,
              })
            : '—'}
        </span>
      ),
    },
    {
      key: 'checks',
      header: t('rde.col.checks'),
      cell: (d) =>
        d.checks_passed + d.checks_failed === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : d.checks_failed > 0 ? (
          <Badge tone="crit">{t('rde.checksFailed', { count: d.checks_failed })}</Badge>
        ) : (
          <Badge tone="ok">{t('rde.checksPassed', { count: d.checks_passed })}</Badge>
        ),
    },
    {
      key: 'delivery',
      header: t('rde.col.delivery'),
      cell: (d) => (
        <div className="space-y-1 whitespace-nowrap">
          {d.verification_status ? <EnumBadge group="verificationStatus" value={d.verification_status} tones={VERIFICATION_STATUS} /> : null}
          {d.delivery_attempts > 0 ? <div className="text-xs text-muted-foreground">{t('rde.attempts', { count: d.delivery_attempts })}</div> : null}
          {d.next_attempt_at ? <div className="text-xs text-muted-foreground">{t('rde.next', { when: formatRelative(d.next_attempt_at) })}</div> : null}
        </div>
      ),
    },
    {
      key: 'format',
      header: t('rde.col.format'),
      cell: (d) => <FormatBadge status={d.format_status} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('rde.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.icann') }]}
        subtitle={t('rde.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        <DraftFormatNotice />
        {data ? <PolicyCard policy={data.policy} /> : null}
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              status || type ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setParams(new URLSearchParams());
                    setCursor(null);
                    setTrail([]);
                  }}
                >
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('rde.filter.status')}
              value={status}
              allLabel={t('common.any')}
              options={options('depositStatus', Object.keys(DEPOSIT_STATUS) as DepositStatus[])}
              onChange={(v) => set('status', v)}
              className="w-full sm:w-52"
            />
            <SelectField
              label={t('rde.filter.type')}
              value={type}
              allLabel={t('common.any')}
              options={options('depositType', DEPOSIT_TYPES)}
              onChange={(v) => set('type', v)}
              className="w-full sm:w-44"
            />
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={4} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('rde.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? <DataTable columns={columns} rows={result.data.items} rowKey={(d) => d.id} empty={<EmptyState title={t('rde.empty')} />} /> : null}
          {result.status === 'success' && (trail.length > 0 || result.data.next) ? (
            <Pager
              canPrevious={trail.length > 0}
              canNext={Boolean(result.data.next)}
              previousLabel={t('rde.newer')}
              nextLabel={t('rde.older')}
              onPrevious={() => {
                setCursor(trail.at(-1) ?? null);
                setTrail(trail.slice(0, -1));
              }}
              onNext={() => {
                setTrail([...trail, cursor]);
                setCursor(result.data.next);
              }}
            />
          ) : null}
        </Section>
      </div>
    </>
  );
}

type DepositFile = EscrowDepositDetail['files'][number];
type Check = EscrowDepositDetail['validation_results'][number];
type Attempt = EscrowDepositDetail['attempts'][number];
type Receipt = EscrowDepositDetail['receipts'][number];

/** `/compliance/icann/rde/:id`: files, validation checks, delivery attempts and receipts of one deposit. */
export function RdeDepositPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getEscrowDeposit(id, { signal }), [id]);
  const d = useRetained(result.status === 'success' ? result.data : undefined);
  useDocumentTitle(d ? `${t('rde.detail.title')} ${formatDateTime(d.watermark)}` : t('rde.detail.title'));
  const crumbs = [{ label: t('crumbs.compliance') }, { label: t('crumbs.icann') }, { label: t('rde.title'), to: BASE }, { label: d ? formatDateTime(d.watermark) : '…' }];
  if (!d) {
    return (
      <>
        <PageHeader title={t('rde.detail.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload} title={t('rde.detail.loadError')} />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }
  const unknown = d.status === 'DELIVERY_UNKNOWN';

  const fileCols: Column<DepositFile>[] = [
    {
      key: 'role',
      header: t('rde.detail.fcol.role'),
      cell: (f) => label('fileRole', f.role),
    },
    {
      key: 'name',
      header: t('rde.detail.fcol.name'),
      cell: (f) => <span className="font-mono text-xs break-all">{f.name}</span>,
    },
    {
      key: 'size',
      header: t('rde.detail.fcol.size'),
      numeric: true,
      cell: (f) => (
        <span className="whitespace-nowrap" title={t('rde.detail.bytes', { count: f.size_bytes })}>
          {formatBytes(f.size_bytes)}
        </span>
      ),
    },
    {
      key: 'sha',
      header: t('rde.detail.fcol.sha'),
      cell: (f) => <CopyText value={f.sha256} display={`${f.sha256.slice(0, 12)}…`} />,
    },
    {
      key: 'enc',
      header: t('rde.detail.fcol.encryption'),
      cell: (f) => <span className="font-mono text-xs">{f.encrypted ? f.encryption : <span className="text-muted-foreground">{t('rde.detail.plain')}</span>}</span>,
    },
  ];
  const checkCols: Column<Check>[] = [
    {
      key: 'check',
      header: t('rde.detail.vcol.check'),
      cell: (v) => <span className="font-mono text-xs">{v.check_name}</span>,
    },
    {
      key: 'outcome',
      header: t('rde.detail.vcol.outcome'),
      cell: (v) => <EnumBadge group="checkOutcome" value={v.outcome} tone={v.outcome === 'PASS' ? 'ok' : 'crit'} />,
    },
    {
      key: 'file',
      header: t('rde.detail.vcol.file'),
      cell: (v) => <span className="font-mono text-xs">{v.file_name ?? '—'}</span>,
    },
    {
      key: 'validator',
      header: t('rde.detail.vcol.validator'),
      cell: (v) => <span className="text-sm">{v.validator}</span>,
    },
    {
      key: 'detail',
      header: t('rde.detail.vcol.detail'),
      cell: (v) => (Object.keys(v.detail).length ? <code className="text-xs break-all">{JSON.stringify(v.detail)}</code> : <span className="text-muted-foreground">—</span>),
    },
  ];
  const attemptCols: Column<Attempt>[] = [
    {
      key: 'n',
      header: t('rde.detail.acol.n'),
      numeric: true,
      cell: (a) => a.attempt,
    },
    {
      key: 'status',
      header: t('rde.detail.acol.status'),
      cell: (a) => <EnumBadge group="attemptStatus" value={a.status} tones={ATTEMPT_TONES} />,
    },
    {
      key: 'sent',
      header: t('rde.detail.acol.sent'),
      cell: (a) => (
        <div className="text-sm whitespace-nowrap">
          {formatDateTime(a.sent_at)} · {a.agent}
          <div>
            <CorrelationLink id={a.correlation_id} label={t('common.correlation')} />
          </div>
        </div>
      ),
    },
    {
      key: 'result',
      header: t('rde.detail.acol.result'),
      cell: (a) => (
        <div className="text-sm">
          {a.external_reference ? <code className="text-xs">{a.external_reference}</code> : null}
          {a.error_code ? (
            <>
              <code className="text-xs">{a.error_code}</code> {a.error_message}
            </>
          ) : null}
        </div>
      ),
    },
  ];
  const receiptCols: Column<Receipt>[] = [
    {
      key: 'at',
      header: t('rde.detail.rcol.recorded'),
      cell: (r) => <span className="text-sm whitespace-nowrap">{formatDateTime(r.recorded_at)}</span>,
    },
    {
      key: 'verification',
      header: t('rde.detail.rcol.verification'),
      cell: (r) => (
        <>
          <EnumBadge group="verificationStatus" value={r.verification_status} tones={VERIFICATION_STATUS} />
          {r.agent_reference ? <div className="mt-1 font-mono text-xs">{r.agent_reference}</div> : null}
        </>
      ),
    },
    {
      key: 'source',
      header: t('rde.detail.rcol.source'),
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1.5 text-sm">
          {label('receiptSource', r.source)} ·{' '}
          <EntityRef label={r.recorded_by_display_name ?? label('actorType', r.recorded_by_type)} id={r.recorded_by_id} />
        </span>
      ),
    },
    {
      key: 'evidence',
      header: t('rde.detail.rcol.evidence'),
      cell: (r) => (
        <div className="text-sm">
          {r.evidence_object_key ? <code className="text-xs break-all">{r.evidence_object_key}</code> : <span className="text-muted-foreground">—</span>}
          {r.note ? <div className="text-muted-foreground">{r.note}</div> : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('rde.detail.heading', {
          type: label('depositType', d.deposit_type),
        })}
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <EnumBadge group="depositStatus" value={d.status} tones={DEPOSIT_STATUS} />
            <FormatBadge status={d.format_status} format={d.format} />
          </span>
        }
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-4">
        {d.format_status !== 'OFFICIAL' ? <DraftFormatNotice /> : null}
        {unknown ? (
          <Callout tone="serious" title={t('rde.detail.unknownTitle')}>
            {t('rde.detail.unknown')}
          </Callout>
        ) : null}
        <Tabs defaultValue="summary">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="summary">{t('rde.detail.tabs.summary')}</TabsTrigger>
            <TabsTrigger value="files">{t('rde.detail.tabs.files')}</TabsTrigger>
            <TabsTrigger value="delivery">{t('rde.detail.tabs.delivery')}</TabsTrigger>
          </TabsList>
          <TabsContent value="summary" className="mt-2">
            <Section title={t('rde.detail.facts')}>
              <KeyValue
                columns={3}
                rows={[
                  [t('rde.detail.deposit'), <CopyText key="i" value={d.id} />],
                  [t('rde.detail.watermark'), formatDateTime(d.watermark)],
                  [t('rde.detail.since'), d.since_watermark ? formatDateTime(d.since_watermark) : null],
                  [
                    t('rde.detail.base'),
                    d.base_deposit_id ? (
                      <Link key="b" to={`${BASE}/${encodeURIComponent(d.base_deposit_id)}`} className="text-primary hover:underline">
                        {t('rde.detail.openBase')}
                      </Link>
                    ) : null,
                  ],
                  [t('rde.detail.scope'), label('escrowScope', d.scope)],
                  [
                    t('rde.detail.policy'),
                    <EntityRef key="p" label={policyLabel(d.policy_key)} to={`/compliance/policies/${encodeURIComponent(d.policy_key)}`} sub={d.policy_version} />,
                  ],
                  [t('rde.detail.records'), d.record_count],
                  [t('rde.detail.deletions'), d.deletion_count],
                  [t('rde.detail.checksum'), d.checksum ? <CopyText key="c" value={d.checksum} display={`${d.checksum.slice(0, 16)}…`} /> : null],
                  [t('rde.detail.encryption'), d.encryption ? <code key="e">{d.encryption}</code> : null],
                  [t('rde.detail.agent'), d.agent],
                  [t('rde.detail.agentRef'), d.external_reference ? <code key="r">{d.external_reference}</code> : null],
                  [t('rde.detail.attempts'), d.delivery_attempts],
                  [t('rde.detail.nextAttempt'), d.next_attempt_at ? `${formatDateTime(d.next_attempt_at)} (${formatRelative(d.next_attempt_at)})` : null],
                  [
                    t('rde.detail.lastError'),
                    d.last_error_code ? (
                      <span key="le">
                        <code>{d.last_error_code}</code> {d.last_error_message}
                      </span>
                    ) : null,
                  ],
                  [
                    t('rde.detail.created'),
                    t('rde.detail.createdBy', {
                      at: formatDateTime(d.created_at),
                      who: d.created_by_display_name ?? d.created_by,
                    }),
                  ],
                  [t('rde.detail.generated'), d.generated_at ? formatDateTime(d.generated_at) : null],
                  [t('rde.detail.validated'), d.validated_at ? formatDateTime(d.validated_at) : null],
                  [t('rde.detail.encrypted'), d.encrypted_at ? formatDateTime(d.encrypted_at) : null],
                  [t('rde.detail.delivered'), d.delivered_at ? formatDateTime(d.delivered_at) : null],
                  [t('rde.detail.confirmed'), d.confirmed_at ? formatDateTime(d.confirmed_at) : null],
                ]}
              />
            </Section>
          </TabsContent>
          <TabsContent value="files" className="mt-2 space-y-4">
            <Section title={t('rde.detail.files')} description={t('rde.detail.filesHint')} flush>
              <DataTable columns={fileCols} rows={d.files} rowKey={(f) => f.id} empty={<EmptyState title={t('rde.detail.noFiles')} />} />
            </Section>
            <Section title={t('rde.detail.checks')} flush>
              <DataTable columns={checkCols} rows={d.validation_results} rowKey={(v) => v.id} empty={<EmptyState title={t('rde.detail.notValidated')} />} />
            </Section>
          </TabsContent>
          <TabsContent value="delivery" className="mt-2">
            <div className="grid gap-4 xl:grid-cols-2">
              <Section title={t('rde.detail.attemptsTitle')} flush>
                <DataTable columns={attemptCols} rows={d.attempts} rowKey={(a) => a.id} empty={<EmptyState title={t('rde.detail.notDelivered')} />} />
              </Section>
              <Section title={t('rde.detail.receipts')} flush>
                <DataTable columns={receiptCols} rows={d.receipts} rowKey={(r) => r.id} empty={<EmptyState title={t('rde.detail.noReceipts')} />} />
              </Section>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
